import {
  Component,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { faAngleLeft, faAngleRight } from '@fortawesome/free-solid-svg-icons';
import { SwiperOptions } from 'swiper/types';

import { ENVIRONMENT, Environment, NewFlowDateFormatOptions, NewFlowDateSelector } from '@environment';
import { isTodayOrTomorrow } from '@usheru-hff/shared/utils';
import { TranslationsService } from '@usheru-hff/shared/utils-translation';
@Component({
  selector: 'ush-showtime-dates',
  templateUrl: './ush-showtime-dates.component.html',
  styleUrls: ['./ush-showtime-dates.component.scss'],
  encapsulation: ViewEncapsulation.Emulated
})
export class UshShowtimeDatesComponent implements OnInit, OnChanges {
  @Input() dates: { [key: string]: string } = null;
  @Output() dateSelected = new EventEmitter<string>();

  @ViewChild('swiper') swiper: any;

  prevArrowSelector: string;
  nextArrowSelector: string;
  prevIcon = faAngleLeft;
  nextIcon = faAngleRight;
  nextArrowDisabled = false;
  prevArrowDisabled = false;

  swiperConfig: SwiperOptions;
  clickedItem: number = null;
  datesUI: { [key: string]: string } = null;
  dateSelectorCustomConfig: NewFlowDateSelector = null;

  constructor(
    @Inject(ENVIRONMENT) private env: Environment,
    private translationService: TranslationsService
  ) {
    this.dateSelectorCustomConfig = this.env?.showtimeStreamingConfig?.newFlowAttributes?.dateSelector || null;
  }

  resetComponent(data: { [key: string]: string }): void {
    this.formatDates(data);
    this.dateClicked(0, this.findKeyWithLowestDate(data));
  }

  ngOnInit(): void {
    this.setSwiperConfig();
    this.dates && this.resetComponent(this.dates);
  }

  findKeyWithLowestDate(dateMap: { [key: string]: string }): string {
    let lowestKey = null;
    for (const key in dateMap) {
      if (!lowestKey || key < lowestKey) {
        lowestKey = key;
      }
    }
    return lowestKey;
  }

  formatDates(dateMap: { [key: string]: string } = null): void {
    if (dateMap) {
      this.datesUI = null;
      this.datesUI = { ...dateMap };
      for (const dateKey in this.datesUI) {
        if (this.datesUI.hasOwnProperty(dateKey)) {
          const todayOrTomorrow = isTodayOrTomorrow(dateKey);
          if (
            (todayOrTomorrow === 'today' || todayOrTomorrow === 'tomorrow') &&
            !this.dateSelectorCustomConfig?.breakDateInNewLines
          ) {
            this.datesUI[dateKey] = todayOrTomorrow;
          }
        }
      }
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.dates?.currentValue && changes?.dates?.previousValue !== changes?.dates?.currentValue) {
      this.resetComponent(changes.dates.currentValue);
      const swiper = this.getSwiperInstance();
      if (swiper) {
        swiper.slideTo(0);
      }
    }
  }

  slidePrev() {
    const swiperInstance = this.getSwiperInstance();
    if (swiperInstance) {
      swiperInstance.slidePrev();
    }
  }

  slideNext() {
    const swiperInstance = this.getSwiperInstance();
    if (swiperInstance) {
      swiperInstance.slideNext();
    }
  }

  private getSwiperInstance() {
    return this.swiper?.nativeElement?.swiper || null;
  }

  private setSwiperConfig() {
    this.setArrowsCssSelectors();
    this.swiperConfig = {
      slidesPerView: 'auto',
      observer: true,
      observeParents: true,
      modules: [myPlugin],
      freeMode: false,
      spaceBetween: this.dateSelectorCustomConfig?.spaceBetweenDates || 5,
      breakpointsBase: 'container',
      scrollbar: {
        snapOnRelease: true,
        enabled: true,
        draggable: true,
        dragSize: 100
      },
      navigation: true,
      injectStyles: [
        `.swiper-horizontal {
          position: relative;
          overflow: hidden;
        }
        .swiper-button-next.swiper-button-disabled,
        .swiper-button-prev.swiper-button-disabled {
          opacity: 0;
        }
        .swiper-button-next, .swiper-button-prev {
          height: 130%;
          top: 12px;
          width: 80px;
          color: transparent;
          ${this.dateSelectorCustomConfig?.enableBlurOnEdges ? '' : 'display:none;'}
        }
        .swiper-button-next {
          right: 0;
          background: ${
            this.dateSelectorCustomConfig?.enableBlurOnEdges
              ? 'linear-gradient(to left, var(--dates-carousel-background, transparent), transparent);'
              : 'transparent'
          }
        }
        .swiper-button-prev {
          left: 0;
          background: ${
            this.dateSelectorCustomConfig?.enableBlurOnEdges
              ? 'linear-gradient(to right, var(--dates-carousel-background, transparent), transparent);'
              : 'transparent'
          }
        }
        `
      ],
      slideToClickedSlide: this.dateSelectorCustomConfig?.animateOnDateSelect || false,
      initialSlide: 0,
      breakpoints: {
        400: {
          spaceBetween: 8
        },
        700: {
          spaceBetween: 12
        },
        1500: {
          spaceBetween: 20
        }
      }
    };
  }

  private setArrowsCssSelectors() {
    const randomId = this.generateRandomId();
    this.prevArrowSelector = `prev-${randomId}`;
    this.nextArrowSelector = `next-${randomId}`;
  }

  private generateRandomId() {
    return Math.random().toString(36).substring(7);
  }

  dateClicked(i: number, dateStr: string): void {
    this.clickedItem = i;
    this.dateSelected.emit(dateStr);
  }

  dateKeyup(event: KeyboardEvent, i: number, dateStr: string): void {
    if (event.key === 'Enter' || event.key === ' ') {
      this.clickedItem = i;
      this.dateSelected.emit(dateStr);
    }
    // arrows left and right foucs next and previous date
    if (event.key === 'ArrowLeft') {
      const preEl = (event.target as HTMLAnchorElement)?.parentElement?.previousElementSibling as HTMLElement;
      if (preEl) {
        (preEl?.children[0] as HTMLElement)?.focus();
      } else {
        const parentList = (event.target as HTMLAnchorElement)?.parentElement?.parentElement as HTMLElement;
        const lastChild = parentList?.children[parentList.children.length - 1] as HTMLElement;
        (lastChild.children[0] as HTMLElement)?.focus();
      }
    }
    if (event.key === 'ArrowRight') {
      const nextEl = (event.target as HTMLAnchorElement)?.parentElement?.nextElementSibling as HTMLElement;
      if (nextEl) {
        (nextEl?.children[0] as HTMLElement)?.focus();
      } else {
        const parentList = (event.target as HTMLAnchorElement)?.parentElement?.parentElement as HTMLElement;
        const firstChild = parentList?.children[0] as HTMLElement;
        (firstChild.children[0] as HTMLElement)?.focus();
      }
    }
  }

  dateValue(index, value): string {
    return value;
  }

  formatDateInLines(dateString: string): string {
    let formattedDate = '';
    if (this.dateSelectorCustomConfig?.breakDateInNewLines) {
      if (this.dateSelectorCustomConfig?.dateFormat === NewFlowDateFormatOptions.MONTH_DATE_DAY) {
        formattedDate = dateString
          .split(' ')
          .map(item => `<span>${item.replace(',', '')}</span>`)
          .reverse()
          .join('<br />');
      } else {
        formattedDate = dateString
          .split(' ')
          .map(item => `<span>${item.replace(',', '')}</span>`)
          .join('<br />');
      }
    } else {
      if (dateString?.toLowerCase() === 'today') {
        formattedDate = this.translationService.translate('public.commons.today', 'Today');
      } else if (dateString?.toLowerCase() === 'tomorrow') {
        formattedDate = this.translationService.translate('public.commons.tomorrow', 'Tomorrow');
      } else {
        formattedDate = dateString;
      }
    }
    return formattedDate;
  }
}

function myPlugin({ swiper, extendParams, on }) {
  extendParams({
    debugger: true
  });
  on('init', () => {
    if (!swiper.params.debugger) return;
    // disable left arrow
  });
  on('sliderMove', (swiper, e) => {
    if (!swiper.params.debugger) return;
    // if not in the beginning, enable left arrow
  });
  on('reachBeginning', () => {
    if (!swiper.params.debugger) return;
  });
  on('reachEnd', () => {
    if (!swiper.params.debugger) return;
    // disable right arrow
  });
  on('tap', (swiper, e) => {
    if (!swiper.params.debugger) return;
  });
}
